// eslint-disable-next-line import/no-extraneous-dependencies
import Model, { Providers, helper } from "@tripian/model";
import React, { useState, useRef, useEffect, useMemo } from "react";
import moment from "moment";
import { allTimeZone } from "./timezones";
import classes from "./GygTourCheckout.module.scss";
import {
  TextField,
  RSelect,
  Button,
  SvgIcons,
  PreLoading,
} from "@tripian/react";
import * as gtag from "../../../../../gtag";

interface IGygTourCheckout {
  user?: Model.User;
  clicked: (data: Providers.Gyg.TourShoppingFormData) => void;
  goBack: () => void;
  tourName?: string;
  tourImage?: string;
  travelDate?: string;
  travelHour?: string;
  travelPrice?: number;
  travelerCount?: number;
  t: (value: Model.TranslationKey) => string;
  onPaymentError?: () => void;
  paymentLoader?: boolean;
}

const GygTourCheckout: React.FC<IGygTourCheckout> = ({
  user,
  clicked,
  goBack,
  tourName,
  tourImage,
  travelDate,
  travelHour,
  travelPrice,
  travelerCount,
  t,
  onPaymentError,
  paymentLoader = false,
}) => {
  moment.locale(window.twindow.langCode);

  const [activeContainer, setActiveContainer] = useState(1);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);

  const contactRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<Providers.Gyg.TourShoppingFormData>({
    user: {
      first_name: user?.firstName || "",
      last_name: user?.lastName || "",
      email: "",
      country_code: "",
      phone_number: "",
      address_line_1: "",
      postal_code: "",
      city: "",
    },
    creditCard: {
      holderName: "",
      number: "",
      cvc: "",
      expiryMonth: "",
      expiryYear: "",
      generationtime: moment().format("YYYY-MM-DDTHH:mm:ss"),
    },
  });

  useEffect(() => {
    if (activeContainer === 1 && contactRef.current) {
      const elementPosition =
        contactRef.current.getBoundingClientRect().top + window.pageYOffset;
      const offset = -100;
      window.scrollTo({ top: elementPosition + offset, behavior: "smooth" });
    } else if (activeContainer === 2 && paymentRef.current) {
      const elementPosition =
        paymentRef.current.getBoundingClientRect().top + window.pageYOffset;
      const offset = -100;
      window.scrollTo({ top: elementPosition + offset, behavior: "smooth" });
    }
  }, [activeContainer]);

  const handleNext = () => {
    if (activeContainer === 1) {
      gtag.googleAnalyticsEvent("booking_process_contact_details_next", { provider: "gyg" });
      setActiveContainer(2);
      setIsPaymentOpen(true);
    }
  };

  const handleEdit = (containerNumber: number) => {
    setActiveContainer(containerNumber);
    if (containerNumber === 2) {
      setIsPaymentOpen(true);
    }
  };

  const isContactButtonDisabled =
    !formData.user.first_name ||
    !formData.user.last_name ||
    !formData.user.email ||
    !formData.user.country_code ||
    !formData.user.phone_number;
  const isPaymentButtonDisabled =
    !formData.creditCard.holderName ||
    !formData.creditCard.number ||
    !formData.creditCard.cvc ||
    !formData.creditCard.expiryMonth ||
    !formData.creditCard.expiryYear;

  const handleConfirmBooking = async () => {
    gtag.googleAnalyticsEvent("booking_process_submit_payment", { provider: "gyg" });
    const selectedCountry = allTimeZone.find(
      atz => atz.Code === formData.user.country_code
    );
    const newFormData = helper.deepCopy(formData);
    newFormData.user.phone_number =
      (selectedCountry?.MobileCode || "") + newFormData.user.phone_number;

    clicked(newFormData);
  };

  const isGuestLogin = useMemo(() => {
    const domain = user?.email.slice(user.email.indexOf("@") + 1).toLowerCase();
    if (domain === "light-tripian.com" || domain === "tripianguest.com") {
      return true;
    }
    return false;
  }, [user?.email]);

  useEffect(() => {
    if (user?.email && !isGuestLogin) {
      setFormData(prev => ({
        ...prev,
        user: {
          ...prev.user,
          email: user.email,
        },
      }));
    }
  }, [user?.email, isGuestLogin]);

  return (
    <>
      {paymentLoader && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
          <PreLoading />
        </div>
      )}
      <div className="row relative mb0">
        <button
          className="m-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors hide-m"
          onClick={() => {
            gtag.googleAnalyticsEvent("booking_process_checkout_close", { provider: "gyg" });
            goBack();
          }}
        >
          <svg
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-x"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Left Column - Contact Details and Payment Details */}
        <div className="col col6-m col12 m0">
          <div className="flex flex-col gap-8 p-6">
            {/* Container 1: Contact Details */}
            <div className="pl-4" ref={contactRef}>
              {activeContainer === 1 ? (
                <div className="row p5 relative m-0 rounded-lg border-[1px] border-solid border-gray-300 min-h-20">
                  <div className="col col12 my4 flex items-center gap-2 !p-0">
                    <div className="rounded-full border-2 w-6 h-6 flex items-center justify-center border-black bg-black text-white text-sm">
                      1
                    </div>
                    <h3 className="font-bold text-lg">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.contactDetails"
                      )}
                    </h3>
                  </div>

                  <div className="col col6">
                    <h4 className="my2">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.firstName"
                      )}
                    </h4>
                    <TextField
                      type="text"
                      name="first_name"
                      value={formData.user.first_name}
                      onChange={event => {
                        setFormData({
                          ...formData,
                          user: {
                            ...formData.user,
                            first_name: event.target.value,
                          },
                        });
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col6">
                    <h4 className="my2">
                      {t("trips.myTrips.localExperiences.tourDetails.lastName")}
                    </h4>
                    <TextField
                      type="text"
                      name="last_name"
                      value={formData.user.last_name}
                      onChange={event => {
                        setFormData({
                          ...formData,
                          user: {
                            ...formData.user,
                            last_name: event.target.value,
                          },
                        });
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col12">
                    <h4 className="my2">
                      {t("trips.myTrips.localExperiences.tourDetails.email")}
                    </h4>
                    <TextField
                      type="email"
                      name="email"
                      value={formData.user.email}
                      onChange={event => {
                        setFormData({
                          ...formData,
                          user: { ...formData.user, email: event.target.value },
                        });
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col12">
                    <h4 className="my2">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.phoneNumber"
                      )}
                    </h4>
                    <div className="flex gap-2">
                      <RSelect
                        options={allTimeZone.map(tz => ({
                          value: tz.Code,
                          label: `${tz.Name} (${tz.MobileCode})`,
                        }))}
                        selectedOptionValue={formData.user.country_code}
                        onSelectedOptionChange={selectedOption => {
                          setFormData({
                            ...formData,
                            user: {
                              ...formData.user,
                              country_code: selectedOption.value,
                            },
                          });
                        }}
                      />
                      <TextField
                        type="text"
                        name="phone_number"
                        value={formData.user.phone_number}
                        onChange={event => {
                          setFormData({
                            ...formData,
                            user: {
                              ...formData.user,
                              phone_number: event.target.value,
                            },
                          });
                        }}
                        placeholder={t(
                          "trips.myTrips.localExperiences.tourDetails.phoneNumber"
                        )}
                        autocomplete="on"
                      />
                    </div>
                  </div>

                  <div className="col col12">
                    <h4 className="my2">
                      {t("trips.myTrips.localExperiences.tourDetails.address")}
                    </h4>
                    <TextField
                      type="text"
                      name="address"
                      value={formData.user.address_line_1}
                      onChange={event => {
                        setFormData({
                          ...formData,
                          user: {
                            ...formData.user,
                            address_line_1: event.target.value,
                          },
                        });
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col6">
                    <h4 className="my2">
                      {t("trips.myTrips.localExperiences.tourDetails.city")}
                    </h4>
                    <TextField
                      type="text"
                      name="city"
                      value={formData.user.city}
                      onChange={event => {
                        setFormData({
                          ...formData,
                          user: { ...formData.user, city: event.target.value },
                        });
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col6">
                    <h4 className="my2">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.postalCode"
                      )}
                    </h4>
                    <TextField
                      type="text"
                      name="postal_code"
                      value={formData.user.postal_code}
                      onChange={event => {
                        setFormData({
                          ...formData,
                          user: {
                            ...formData.user,
                            postal_code: event.target.value,
                          },
                        });
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col12 center mt5">
                    <Button
                      className="w-full"
                      onClick={handleNext}
                      text={t(
                        "trips.myTrips.localExperiences.tourDetails.next"
                      )}
                      color="primary"
                      disabled={isContactButtonDisabled}
                    />
                  </div>
                </div>
              ) : (
                <div className="row px-5 relative m-0 rounded-lg border-[1px] border-solid border-gray-300 min-h-20">
                  <div className="col col12 my4 flex items-center gap-2">
                    <div className="rounded-full border-[1px] w-5 h-5 flex items-center justify-center border-black text-sm">
                      1
                    </div>
                    <h3 className="font-bold text-lg">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.contactDetails"
                      )}
                    </h3>
                  </div>
                  <div className="absolute right-4 top-3 border-none w-[35px] h-[35px] cursor-pointer font-medium">
                    <button
                      className="hover:underline"
                      onClick={() => handleEdit(1)}
                    >
                      {t("trips.myTrips.localExperiences.tourDetails.edit")}
                    </button>
                  </div>

                  <div className="col col12 mb2">
                    <p className="font-bold">{`${formData.user.first_name} ${formData.user.last_name}`}</p>
                  </div>
                  <div className="col col12 flex items-center gap-2 mb0">
                    <h4 className="my2 font-bold">
                      {t("trips.myTrips.localExperiences.tourDetails.email")}:
                    </h4>
                    <p>{formData.user.email}</p>
                  </div>
                  <div className="col col12 flex items-center gap-2 mb2">
                    <h4 className="my2 font-bold">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.phoneNumber"
                      )}
                      :
                    </h4>
                    <p>
                      {allTimeZone.find(
                        tz => tz.Code === formData.user.country_code
                      )?.MobileCode || formData.user.country_code}{" "}
                      {formData.user.phone_number}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Container 2: Payment Details */}
            <div className="pl-4" ref={paymentRef}>
              {activeContainer === 2 && isPaymentOpen ? (
                <div className="row p5 relative m-0 rounded-lg border-[1px] border-solid border-gray-300 min-h-20">
                  <div className="col col12 my4 flex items-center gap-2 !p-0">
                    <div className="rounded-full border-2 w-6 h-6 flex items-center justify-center border-black bg-black text-white text-sm">
                      2
                    </div>
                    <h3 className="font-bold text-lg">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.paymentDetails"
                      )}
                    </h3>
                  </div>

                  <span className={classes.paymentInfo}>
                    {t('trips.myTrips.localExperiences.tourDetails.paymentAreSecureAndEncrypted')}
                  </span>

                  <div className="col col12">
                    <h4 className="my2">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.holderName"
                      )}
                    </h4>
                    <TextField
                      type="text"
                      name="credit_card_fullname"
                      value={formData.creditCard.holderName}
                      onChange={event => {
                        setFormData({
                          ...formData,
                          creditCard: {
                            ...formData.creditCard,
                            holderName: event.target.value,
                          },
                        });
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col12">
                    <h4 className="my2">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.cardNumber"
                      )}
                    </h4>
                    <TextField
                      type="number"
                      name="credit_card_no"
                      value={formData.creditCard.number}
                      onChange={event => {
                        const value = event.target.value;
                        if (value.length <= 16) {
                          setFormData({
                            ...formData,
                            creditCard: {
                              ...formData.creditCard,
                              number: value,
                            },
                          });
                        }
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col4">
                    <h4 className="my2">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.expiryMonth"
                      )}
                    </h4>
                    <TextField
                      type="number"
                      name="expiryMonth"
                      value={formData.creditCard.expiryMonth}
                      onChange={event => {
                        const value = event.target.value;
                        if (value.length <= 2) {
                          setFormData({
                            ...formData,
                            creditCard: {
                              ...formData.creditCard,
                              expiryMonth: value,
                            },
                          });
                        }
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col4">
                    <h4 className="my2">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.expiryYear"
                      )}
                    </h4>
                    <TextField
                      type="number"
                      name="expiryYear"
                      value={formData.creditCard.expiryYear}
                      onChange={event => {
                        setFormData({
                          ...formData,
                          creditCard: {
                            ...formData.creditCard,
                            expiryYear: event.target.value,
                          },
                        });
                      }}
                      autocomplete="on"
                    />
                  </div>

                  <div className="col col4">
                    <h4 className="my2">CVC</h4>
                    <TextField
                      type="number"
                      name="credit_card_cvc"
                      value={formData.creditCard.cvc}
                      onChange={event => {
                          const value = event.target.value;
                          if (value.length <= 3) {
                              setFormData({
                                  ...formData,
                                  creditCard: {
                                      ...formData.creditCard,
                                      cvc: event.target.value,
                                  },
                              });
                          }
                      }}
                      autocomplete="on"
                      max="3"
                    />
                  </div>

                  <div className="col col12 center mt5">
                    <Button
                      onClick={handleConfirmBooking}
                      text={
                        paymentLoader
                          ? "Processing..."
                          : t(
                              "trips.myTrips.localExperiences.tourDetails.confirmBooking"
                            )
                      }
                      color={
                        isPaymentButtonDisabled || paymentLoader
                          ? "disabled"
                          : "primary"
                      }
                      disabled={isPaymentButtonDisabled || paymentLoader}
                      className="w-full"
                    />
                  </div>
                </div>
              ) : (
                <div className="row px-5 relative m-0 rounded-lg border-[1px] border-solid border-gray-300 min-h-20">
                  <div className="col col12 my4 flex items-center gap-2">
                    <div className="rounded-full border-[1px] w-5 h-5 flex items-center justify-center border-black text-sm">
                      2
                    </div>
                    <h3 className="font-bold text-lg">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.paymentDetails"
                      )}
                    </h3>
                  </div>
                  <div className="absolute right-4 top-3 border-none w-[35px] h-[35px] cursor-pointer font-medium">
                    <button
                      className="hover:underline"
                      onClick={() => handleEdit(2)}
                    >
                      {t("trips.myTrips.localExperiences.tourDetails.edit")}
                    </button>
                  </div>

                  {formData.creditCard.holderName && (
                    <>
                      <div className="col col12 mb2">
                        <p className="font-bold">
                          {formData.creditCard.holderName}
                        </p>
                      </div>
                      <div className="col col12 flex items-center gap-2 mb0">
                        <h4 className="my2 font-bold">
                          {t(
                            "trips.myTrips.localExperiences.tourDetails.cardNumber"
                          )}
                          :
                        </h4>
                        <p>
                          **** **** **** {formData.creditCard.number.slice(-4)}
                        </p>
                      </div>
                      <div className="col col12 flex items-center gap-2 mb2">
                        <h4 className="my2 font-bold">Expiry:</h4>
                        <p>
                          {formData.creditCard.expiryMonth}/
                          {formData.creditCard.expiryYear}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Travel Summary */}
        <div className="col col6-m col12 m0 bg-[#f5f5f5] hide-s">
          <div className="sticky top-0 right-0 z-20 flex justify-end w-full bg-transparent">
            <button
              className="m-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              onClick={() => {
                gtag.googleAnalyticsEvent("booking_process_checkout_close", { provider: "gyg" });
                goBack();
              }}
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-x"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="sticky top-28">
            <div className="relative m-0 p-0">
              <div className="rounded-lg bg-white shadow-sm m-6">
                <div className="p-5 text-black">
                  <div>
                    <div className="photo-row flex gap-4 items-start">
                      <img
                        className="w-20 h-20 rounded-md object-cover overflow-hidden shrink-0"
                        src={
                          helper.getYourGuideImageArrayFormat(
                            [tourImage || ""],
                            "146"
                          )[0]
                        }
                        alt=""
                      />
                      <div>
                        <span className="text-sm font-bold line-clamp-2">
                          {tourName}
                        </span>
                      </div>
                    </div>
                    <div className="product-info mt-4 flex flex-col text-sm">
                      <ul className="list-none p-0 m-0 flex flex-col gap-2 text-sm font-medium mb-2">
                        <li className="flex items-start">
                          <SvgIcons.Avatar
                            className="mt-[0.125rem] mr-2"
                            size="0.875rem"
                            fill="#000"
                          />
                          <span>
                            {travelerCount}{" "}
                            {t(
                              "trips.myTrips.localExperiences.tourDetails.person"
                            )}
                          </span>
                        </li>
                        <li className="flex items-start">
                          <SvgIcons.Calendar
                            className="mt-[0.175rem] mr-2"
                            size="0.875rem"
                            fill="#000"
                          />
                          <span>
                            {moment(travelDate).format("ddd, MMM DD, YYYY")}
                          </span>
                          {travelHour && (
                            <>
                              <span className="whitespace-pre-wrap px-1">
                                {" "}
                                •{" "}
                              </span>
                              <span>
                                {moment(travelHour).format("hh:mm A")}
                              </span>
                            </>
                          )}
                        </li>
                      </ul>
                    </div>
                    {travelPrice && travelPrice > 0 && (
                      <div className="pricing-row flex items-end gap-2 flex-row-reverse text-end">
                        <div className="font-bold">${travelPrice}</div>
                      </div>
                    )}
                  </div>
                  {travelPrice && travelPrice > 0 && (
                    <>
                      <hr className="my-4" style={{ opacity: 0.6 }} />
                      <div className="flex justify-between font-bold w-full text-lg">
                        <div>
                          {t(
                            "trips.myTrips.localExperiences.tourDetails.totalPrice"
                          )}
                        </div>
                        <div>${travelPrice}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GygTourCheckout;
